import { inject, Injectable } from "@angular/core";
import {
  catchError,
  concatMap,
  from,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  toArray,
} from "rxjs";
import { EnrichmentService } from "@app/shared/services/enrichment.service";
import {
  EnrichmentDetail,
  EnrichmentPriceItem,
  ShelfRef,
} from "@app/shared/models/enrichment-detail.model";
import {
  IEnrichmentPriceSaveOptions,
  IEnrichmentReadOptions,
} from "@app/shared/models/enrichment.model";
import {
  detailToModel,
  modelToDetail,
} from "@app/shared/mappers/enrichment-detail.adapter";
import {
  buildPriceSaveBody,
  NOTHING_AUTHORED,
} from "../../../edit/enrichment-edit-package/price-save-body.util";
import { PriceSaveDecision } from "../../../edit/enrichment-edit-package/save-confirm.util";
import { groupByOffering } from "../enrichment-list.transformer";
import { PriceRowKey } from "../enrichment-list.types";
import { refusedForRoleSwitch, WriteScope } from "../write-scope";

export interface CategoryAssignPriceFailure {
  priceId: string;
  error: unknown;
}

/** Outcome of binding categories to a single package. */
export interface CategoryAssignOutcome {
  packageId: string;
  ok: boolean;
  /** How many of the package's prices were actually written. */
  pricesUpdated?: number;
  pricesUnchanged?: number;
  failedPrices?: CategoryAssignPriceFailure[];
  notSentPrices?: string[];
  error?: unknown;
}

/** Roll-up of a bulk assign run. */
export interface CategoryAssignSummary {
  outcomes: CategoryAssignOutcome[];
  succeeded: number;
  failed: number;
  pricesUpdated: number;
  pricesUnchanged: number;
  pricesFailed: number;
  pricesNotSent: number;
}

const ASSIGN_CONCURRENCY = 4;

type PriceWrite =
  | { result: "written"; priceId: string }
  | { result: "failed"; priceId: string; error: unknown }
  | { result: "notSent"; priceId: string };

/**
 * Writes each selected price through `PATCH /enrichment/price/{id}`, because
 * the BFF ignores `prices` on `PATCH /enrichment/{id}`. Add-only: the selection
 * spans prices with different shelves, so removal stays in the editor.
 */
@Injectable({ providedIn: "root" })
export class CategoryAssignService {
  private readonly enrichmentService = inject(EnrichmentService);

  assignShelves(
    rows: readonly PriceRowKey[],
    shelvesToAdd: readonly ShelfRef[],
    scope: WriteScope,
    decision?: PriceSaveDecision,
  ): Observable<CategoryAssignSummary> {
    const packages = [...groupByOffering(rows)];
    if (packages.length === 0 || shelvesToAdd.length === 0) {
      return of(summarizeAssign([]));
    }
    const read: IEnrichmentReadOptions = {
      channel: scope.channel,
      channelTransport: "header",
    };
    const save: IEnrichmentPriceSaveOptions = {
      ...read,
      keepLifecycleStatus: decision?.keepLifecycleStatus ?? false,
    };
    return from(packages).pipe(
      mergeMap(
        ([packageId, priceIds]) =>
          this.assignOne(packageId, priceIds, shelvesToAdd, scope, read, save),
        ASSIGN_CONCURRENCY,
      ),
      toArray(),
      map(summarizeAssign),
    );
  }

  private assignOne(
    packageId: string,
    priceIds: readonly string[],
    shelvesToAdd: readonly ShelfRef[],
    scope: WriteScope,
    read: IEnrichmentReadOptions,
    save: IEnrichmentPriceSaveOptions,
  ): Observable<CategoryAssignOutcome> {
    if (!scope.holds()) return of(notSentOutcome(packageId, priceIds));
    return this.enrichmentService.getEnrichmentById(packageId, read).pipe(
      switchMap((res) => {
        if (!scope.holds()) return of(notSentOutcome(packageId, priceIds));
        const detail = res?.data;
        if (!detail) {
          return of(
            unwrittenOutcome(
              packageId,
              priceIds,
              new Error("Package detail not found"),
            ),
          );
        }
        const wanted = new Set(priceIds);
        const targeted = (detail.prices ?? []).filter((p) => wanted.has(p.id));
        const changing = new Set(
          targeted
            .filter((p) => !hasEveryShelf(p.shelves ?? [], shelvesToAdd))
            .map((p) => p.id),
        );
        const withShelves: EnrichmentDetail = {
          ...detail,
          prices: (detail.prices ?? []).map((p) =>
            changing.has(p.id)
              ? { ...p, shelves: unionShelves(p.shelves ?? [], shelvesToAdd) }
              : p,
          ),
        };
        const prices = (withMoneyPrices(withShelves).prices ?? []).filter((p) =>
          changing.has(p.id),
        );
        return from(prices).pipe(
          concatMap((price) =>
            scope.holds()
              ? this.writePrice(price, save)
              : of<PriceWrite>({ result: "notSent", priceId: price.id }),
          ),
          toArray(),
          map((writes) =>
            packageOutcome(packageId, writes, targeted.length - changing.size),
          ),
        );
      }),
      catchError((error: unknown) =>
        of(
          scope.holds()
            ? unwrittenOutcome(packageId, priceIds, error)
            : notSentOutcome(packageId, priceIds),
        ),
      ),
    );
  }

  private writePrice(
    price: EnrichmentPriceItem,
    save: IEnrichmentPriceSaveOptions,
  ): Observable<PriceWrite> {
    return this.enrichmentService
      .saveEnrichmentPrice(
        price.id,
        buildPriceSaveBody(price, NOTHING_AUTHORED),
        save,
      )
      .pipe(
        map((): PriceWrite => ({ result: "written", priceId: price.id })),
        catchError((error: unknown) =>
          of<PriceWrite>(
            refusedForRoleSwitch(error)
              ? { result: "notSent", priceId: price.id }
              : { result: "failed", priceId: price.id, error },
          ),
        ),
      );
  }
}

function packageOutcome(
  packageId: string,
  writes: readonly PriceWrite[],
  pricesUnchanged: number,
): CategoryAssignOutcome {
  const failedPrices = writes.flatMap((write) =>
    write.result === "failed"
      ? [{ priceId: write.priceId, error: write.error }]
      : [],
  );
  const notSentPrices = writes.flatMap((write) =>
    write.result === "notSent" ? [write.priceId] : [],
  );
  return {
    packageId,
    ok: failedPrices.length === 0 && notSentPrices.length === 0,
    pricesUpdated: writes.filter((write) => write.result === "written").length,
    pricesUnchanged,
    failedPrices,
    notSentPrices,
  };
}

/** The editor's adapter round-trip, which re-serializes prices[].price as TMF
 *  Money. The BE rejects a legacy bare number with a Go unmarshal error
 *  ("cannot unmarshal number into ... v5.Money"). */
function withMoneyPrices(detail: EnrichmentDetail): EnrichmentDetail {
  return modelToDetail(detailToModel(detail), detail);
}

function unwrittenOutcome(
  packageId: string,
  priceIds: readonly string[],
  error: unknown,
): CategoryAssignOutcome {
  return {
    packageId,
    ok: false,
    pricesUpdated: 0,
    pricesUnchanged: 0,
    failedPrices: priceIds.map((priceId) => ({ priceId, error })),
    error,
  };
}

function notSentOutcome(
  packageId: string,
  priceIds: readonly string[],
): CategoryAssignOutcome {
  return {
    packageId,
    ok: false,
    pricesUpdated: 0,
    pricesUnchanged: 0,
    failedPrices: [],
    notSentPrices: [...priceIds],
  };
}

export function unwrittenSummary(
  rows: readonly PriceRowKey[],
  error: unknown,
): CategoryAssignSummary {
  return summarizeAssign(
    [...groupByOffering(rows)].map(([packageId, priceIds]) =>
      unwrittenOutcome(packageId, priceIds, error),
    ),
  );
}

function summarizeAssign(
  outcomes: CategoryAssignOutcome[],
): CategoryAssignSummary {
  return {
    outcomes,
    succeeded: outcomes.filter((o) => o.ok).length,
    failed: outcomes.filter((o) => !o.ok).length,
    pricesUpdated: outcomes.reduce((n, o) => n + (o.pricesUpdated ?? 0), 0),
    pricesUnchanged: outcomes.reduce((n, o) => n + (o.pricesUnchanged ?? 0), 0),
    pricesFailed: outcomes.reduce(
      (n, o) => n + (o.failedPrices?.length ?? 0),
      0,
    ),
    pricesNotSent: outcomes.reduce(
      (n, o) => n + (o.notSentPrices?.length ?? 0),
      0,
    ),
  };
}

/** Union `existing` ⋃ `toAdd` by id, keeping the existing order first and
 *  emitting only the BE-facing `{ id, name }` shape (drops the FE-only
 *  `__name` locale mirror, mirroring the adapter's PATCH serialization). */
export function unionShelves(
  existing: readonly ShelfRef[],
  toAdd: readonly ShelfRef[],
): ShelfRef[] {
  const seen = new Set<string>();
  const out: ShelfRef[] = [];
  const push = (s: ShelfRef): void => {
    if (!s?.id || seen.has(s.id)) return;
    seen.add(s.id);
    out.push({ id: s.id, name: s.name });
  };
  for (const s of existing) push(s);
  for (const s of toAdd) push(s);
  return out;
}

export function hasEveryShelf(
  shelves: readonly ShelfRef[],
  wanted: readonly ShelfRef[],
): boolean {
  const held = unionShelves(shelves, []);
  return unionShelves(held, wanted).length === held.length;
}
